import {InjectionToken, Injector, signal} from "@angular/core";
import {AppService} from "./services/app.service";
import {AuthService} from "./auth";
import {NavService} from "./services/nav.service";
import {CaptureError, IMessageCode} from "@mindpath/shared";
import {Span} from "@sentry/angular";
import {AnalyticsService} from "./analytics/analytics.service";
import * as Sentry from "@sentry/angular";

let ready = false;


function callInit(parentSpan: Span, initName: string, fn: () => Promise<unknown>) {
  return Sentry.startSpan({
    name: initName,
    parentSpan,
    op: `app.init:${initName}`,
  }, async span => {
    try {
      await fn();
    } catch (e) {
      span.setStatus({
        code: 2, // 2 = ERROR
      });
      throw CaptureError(e, {
        details: `App initializer: ${initName}`,
        noLog: true,
      })
    }
  });
}

const {promise, resolve, reject} = Promise.withResolvers<void>();
const loadInfo$ = signal({
  loadStart: Date.now(),
  loadEnd: undefined as number | undefined,
  completed: false as boolean,
  error: undefined as {
    code: IMessageCode,
    from: unknown
  } | undefined
})

export async function InitializeApp(injector: Injector) {

  let app: AppService | undefined;
  return Sentry.startSpan({
    name: 'App Initialization',
    op: 'app.init',
  }, async span => {

    try {

      const auth = injector.get(AuthService);
      const analytics = injector.get(AnalyticsService);
      const nav = injector.get(NavService);
      app = injector.get(AppService);

      await ([
        ['Auth', auth.init.bind(auth)],
        ['Analytics', analytics.init.bind(analytics)],
        ['Nav', nav.init.bind(nav)],
        ['App', app.init.bind(app)]
      ] as const).reduce((p, [category, fn]) =>
        p.then(() => callInit(span, category, fn)), Promise.resolve());

      ready = true;
      loadInfo$.update(l => {
        return {
          ...l,
          completed: true
        }
      });
      resolve();
    } catch (e) {
      const error = CaptureError(e, {noLog: true});

      app?.errorOccurred(error, {
        devDetails: {
          message: `Error occurred while initializing the application`,
        }
      });
      ready = false;
      loadInfo$.update(l => {
        return {
          ...l,
          error: {
            code: error,
            from: e
          }
        }
      });
      reject(e);
    } finally {
      loadInfo$.update(l => {
        return {
          ...l,
          loadEnd: Date.now(),
        }
      })
    }
  });
}


export const AppReady = new InjectionToken(`AppReady`, {
  providedIn: 'root',
  factory: () => promise
});

export const AppReadyChecker = new InjectionToken(`AppReadyChecker`, {
  providedIn: 'root',
  factory: () => (() => ready)
});

export const AppLoadInfo = new InjectionToken(`AppLoadInfo`, {
  providedIn: 'root',
  factory: () => loadInfo$.asReadonly()
});

